import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { ShieldCheck, Users, CalendarPlus, Radio, GraduationCap, ShoppingBag, Search, Download, FileText, Upload, Image as ImageIcon, Save, Mail, Trash2, CheckCircle2, Inbox } from "lucide-react";

const TABS = [
  { k: "contenuti", label: "Contenuti", icon: FileText },
  { k: "messaggi", label: "Messaggi", icon: Inbox },
  { k: "soci", label: "Registro Soci", icon: Users },
  { k: "eventi", label: "Eventi", icon: CalendarPlus },
  { k: "podcast", label: "Podcast", icon: Radio },
  { k: "corsi", label: "Corsi", icon: GraduationCap },
  { k: "shop", label: "Shop", icon: ShoppingBag },
];

export default function Admin() {
  const { user, loading, login } = useAuth();
  const [tab, setTab] = useState("contenuti");
  const [stats, setStats] = useState(null);
  const [unread, setUnread] = useState(0);

  useEffect(() => {
    if (user?.is_admin) {
      api.get("/admin/stats").then(r => setStats(r.data)).catch(() => {});
      api.get("/admin/contact_messages").then(r => setUnread((r.data||[]).filter(m => !m.read).length)).catch(() => {});
    }
  }, [user]);

  if (loading) return <div className="p-20 text-center text-white/70">Caricamento...</div>;
  if (!user) return (
    <section className="mx-auto max-w-md px-4 py-24 text-center">
      <ShieldCheck size={48} className="mx-auto text-[#FFFFFF]"/>
      <h1 className="mt-4 font-display text-4xl uppercase">Area riservata</h1>
      <p className="mt-3 text-white/70">Effettua il login per accedere.</p>
      <button onClick={login} className="brutal-btn brutal-btn-primary mt-6">Login con Google</button>
    </section>
  );
  if (!user.is_admin) return (
    <section className="mx-auto max-w-md px-4 py-24 text-center">
      <h1 className="font-display text-4xl uppercase text-white">Accesso negato</h1>
      <p className="mt-3 text-white/70">Il tuo account ({user.email}) non ha permessi di amministrazione.</p>
    </section>
  );

  return (
    <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-10">
      <div className="mb-8 flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <div className="tag-pill mb-2"><ShieldCheck size={12}/> Admin dashboard</div>
          <h1 className="font-display text-4xl sm:text-5xl uppercase" data-testid="admin-heading">Gestione Flow.Bologna</h1>
          <p className="text-white/70 mt-2 text-sm">Ciao <b>{user.name}</b>, sei loggato come admin.</p>
        </div>
        {stats && (
          <div className="grid grid-cols-4 gap-2 text-center">
            <Stat label="DAMACO" value={stats.soci_damaco} color="#FFFFFF"/>
            <Stat label="ROCCA" value={stats.soci_rocca} color="#FFFFFF"/>
            <Stat label="Pagati" value={stats.soci_paid} color="#FFFFFF"/>
            <Stat label="Eventi" value={stats.events} color="#FFFFFF"/>
          </div>
        )}
      </div>

      <div className="flex flex-wrap gap-2 mb-6 border-b border-[#FFFFFF]">
        {TABS.map(({k, label, icon: Icon}) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`tab-${k}`}
            className={`px-4 py-3 text-sm font-semibold uppercase tracking-wider flex items-center gap-2 border-b-2 -mb-px ${
              tab===k ? "border-[#FFFFFF] text-[#FFFFFF]" : "border-transparent text-white/70 hover:text-white"
            }`}>
            <Icon size={14}/> {label}
            {k === "messaggi" && unread > 0 && (
              <span className="ml-1 min-w-[20px] h-5 px-1 flex items-center justify-center bg-white text-black text-xs rounded-full font-mono" data-testid="messaggi-badge">
                {unread}
              </span>
            )}
          </button>
        ))}
      </div>

      {tab === "contenuti" && <ContenutiTab/>}
      {tab === "messaggi" && <MessaggiTab/>}
      {tab === "soci" && <SociTab/>}
      {tab === "eventi" && <EventiTab/>}
      {tab === "podcast" && <PodcastTab/>}
      {tab === "corsi" && <CorsiTab/>}
      {tab === "shop" && <ShopTab/>}
    </section>
  );
}

function Stat({ label, value, color }) {
  return (
    <div className="card p-3 min-w-[80px]">
      <div className="text-xs font-mono uppercase" style={{color}}>{label}</div>
      <div className="font-display text-2xl">{value}</div>
    </div>
  );
}

/* ---------- CONTENUTI ---------- */
const CONTENT_FIELDS = [
  { group: "Home", fields: [
    { k: "hero_tag", label: "Etichetta hero (piccola sopra il titolo)", type: "input" },
    { k: "hero_title_1", label: "Titolo hero — riga 1", type: "input" },
    { k: "hero_title_2", label: "Titolo hero — riga 2", type: "input" },
    { k: "hero_title_3", label: "Titolo hero — riga 3", type: "input" },
    { k: "hero_subtitle", label: "Sottotitolo hero", type: "textarea" },
    { k: "bio_tag", label: "Etichetta 'Chi siamo'", type: "input" },
    { k: "bio_title", label: "Titolo 'Chi siamo'", type: "input" },
    { k: "bio_text", label: "Testo 'Chi siamo'", type: "textarea" },
  ]},
  { group: "Footer & Contatti", fields: [
    { k: "footer_text", label: "Testo indirizzo (footer)", type: "textarea" },
    { k: "contact_email", label: "Email di contatto", type: "input" },
    { k: "social_instagram", label: "URL Instagram", type: "input" },
    { k: "social_facebook", label: "URL Facebook", type: "input" },
    { k: "social_tiktok", label: "URL TikTok", type: "input" },
    { k: "social_youtube", label: "URL YouTube", type: "input" },
    { k: "social_twitter", label: "URL X (Twitter)", type: "input" },
    { k: "social_spotify", label: "URL Spotify", type: "input" },
  ]},
  { group: "Eventi", fields: [
    { k: "eventi_title", label: "Titolo pagina Eventi", type: "input" },
    { k: "eventi_subtitle", label: "Descrizione pagina Eventi", type: "textarea" },
  ]},
  { group: "Podcast", fields: [
    { k: "podcast_title", label: "Titolo pagina Podcast", type: "input" },
    { k: "podcast_subtitle", label: "Descrizione pagina Podcast", type: "textarea" },
  ]},
  { group: "Tesseramento", fields: [
    { k: "tesseramento_title", label: "Titolo pagina Tesseramento", type: "input" },
    { k: "tesseramento_subtitle", label: "Descrizione pagina Tesseramento", type: "textarea" },
    { k: "privacy_text", label: "Testo informativa Privacy (usato se non è caricato il PDF)", type: "textarea" },
  ]},
  { group: "Contatti", fields: [
    { k: "contatti_title", label: "Titolo pagina Contatti", type: "input" },
    { k: "contatti_subtitle", label: "Descrizione pagina Contatti", type: "textarea" },
  ]},
  { group: "Corsi", fields: [
    { k: "corsi_title", label: "Titolo pagina Corsi", type: "input" },
    { k: "corsi_subtitle", label: "Descrizione pagina Corsi", type: "textarea" },
  ]},
  { group: "Shop", fields: [
    { k: "shop_title", label: "Titolo pagina Shop", type: "input" },
    { k: "shop_subtitle", label: "Descrizione pagina Shop", type: "textarea" },
  ]},
];

function ContenutiTab() {
  const [data, setData] = useState(null);
  const [dirty, setDirty] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/site_content").then(r => setData(r.data)); }, []);

  const set = (k, v) => { setData(d => ({...d, [k]: v})); setDirty(x => ({...x, [k]: true})); };

  const save = async () => {
    const payload = {};
    for (const k of Object.keys(dirty)) payload[k] = data[k];
    if (!Object.keys(payload).length) return toast.info("Nessuna modifica");
    setBusy(true);
    try {
      await api.patch("/admin/site_content", payload);
      toast.success("Contenuti salvati");
      setDirty({});
    } catch { toast.error("Errore salvataggio"); }
    setBusy(false);
  };

  const uploadFile = async (field, accept) => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = accept;
    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;
      const fd = new FormData();
      fd.append("file", file);
      setBusy(true);
      try {
        const { data: up } = await api.post("/uploads", fd, { headers: {"Content-Type": "multipart/form-data"} });
        const fullUrl = process.env.REACT_APP_BACKEND_URL + up.url;
        set(field, fullUrl);
        await api.patch("/admin/site_content", { [field]: fullUrl });
        toast.success("File caricato");
        setDirty(x => ({...x, [field]: false}));
      } catch (e) {
        toast.error("Errore upload: " + (e.response?.data?.detail || e.message));
      }
      setBusy(false);
    };
    input.click();
  };

  if (!data) return <div className="text-white/70">Caricamento...</div>;

  return (
    <div className="space-y-8">
      {/* Assets uploads */}
      <div className="card p-5">
        <h3 className="font-display text-2xl uppercase text-white mb-4">Logo, foto & privacy</h3>
        <div className="grid md:grid-cols-3 gap-4">
          <AssetRow label="Logo del sito" value={data.logo_url} onUpload={() => uploadFile("logo_url","image/*")} onClear={() => { set("logo_url", null); api.patch("/admin/site_content",{logo_url:null}); }} type="image"/>
          <AssetRow label="Foto o Video Home (Hero)" value={data.hero_image_url} onUpload={() => uploadFile("hero_image_url","image/*,video/*")} onClear={() => { set("hero_image_url", null); api.patch("/admin/site_content",{hero_image_url:null}); }} type={data.hero_image_url && /\.(mp4|webm|mov|m4v)(\?|$)/i.test(data.hero_image_url) ? "video" : "image"}/>
          <AssetRow label="PDF Informativa Privacy" value={data.privacy_pdf_url} onUpload={() => uploadFile("privacy_pdf_url","application/pdf")} onClear={() => { set("privacy_pdf_url", null); api.patch("/admin/site_content",{privacy_pdf_url:null}); }} type="pdf"/>
        </div>
        <p className="mt-3 text-xs text-white/50 font-mono uppercase tracking-widest">Sostituisci in qualsiasi momento. Il sito si aggiornerà subito.</p>
      </div>

      {/* Text fields */}
      {CONTENT_FIELDS.map(grp => (
        <div key={grp.group} className="card p-5">
          <h3 className="font-display text-2xl uppercase text-white mb-4">{grp.group}</h3>
          <div className="grid gap-4">
            {grp.fields.map(f => (
              <div key={f.k}>
                <label className="block text-xs font-mono uppercase tracking-widest text-white mb-1">{f.label}</label>
                {f.type === "textarea" ? (
                  <textarea rows={4} value={data[f.k] || ""} onChange={e => set(f.k, e.target.value)}
                    data-testid={`content-${f.k}`}
                    className="w-full bg-black border border-white/40 focus:border-white px-3 py-2 rounded text-white"/>
                ) : (
                  <input value={data[f.k] || ""} onChange={e => set(f.k, e.target.value)}
                    data-testid={`content-${f.k}`}
                    className="w-full bg-black border border-white/40 focus:border-white px-3 py-2 rounded text-white"/>
                )}
              </div>
            ))}
          </div>
        </div>
      ))}

      <div className="sticky bottom-4 flex justify-end">
        <button onClick={save} disabled={busy} className="brutal-btn brutal-btn-primary shadow-[0_10px_30px_rgba(0,0,0,0.7)]" data-testid="content-save">
          <Save size={16}/> {busy ? "Salvataggio..." : "Salva tutte le modifiche"}
        </button>
      </div>
    </div>
  );
}

function AssetRow({ label, value, onUpload, onClear, type }) {
  return (
    <div className="border border-white/40 p-3 rounded">
      <div className="text-xs font-mono uppercase tracking-widest text-white mb-2">{label}</div>
      <div className="aspect-video bg-black border border-white/20 flex items-center justify-center overflow-hidden mb-3">
        {value ? (
          type === "pdf" ? (
            <a href={value} target="_blank" rel="noopener noreferrer" className="text-white underline text-sm">Apri PDF</a>
          ) : type === "video" ? (
            <video src={value} controls className="w-full h-full object-contain"/>
          ) : (
            <img src={value} alt={label} className="w-full h-full object-contain"/>
          )
        ) : (
          <div className="text-white/40 text-center">
            <ImageIcon size={28} className="mx-auto"/>
            <div className="mt-2 text-xs">Nessun file</div>
          </div>
        )}
      </div>
      <div className="flex gap-2">
        <button onClick={onUpload} className="brutal-btn brutal-btn-outline text-xs flex-1">
          <Upload size={14}/> Carica
        </button>
        {value && <button onClick={onClear} className="brutal-btn brutal-btn-outline text-xs">Rimuovi</button>}
      </div>
    </div>
  );
}

/* ---------- Shared Edit Modal ---------- */
function EditModal({ title, item, fields, onSave, onClose }) {
  const [form, setForm] = useState({...item});
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try { await onSave(form); toast.success("Modifiche salvate"); onClose(); }
    catch (e) { toast.error("Errore salvataggio"); }
    setBusy(false);
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4" onClick={onClose}>
      <div className="card p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <h3 className="font-display text-2xl uppercase text-white mb-4">{title}</h3>
        <div className="grid gap-3">
          {fields.map(f => {
            if (f.type === "hidden") return null;
            if (f.type === "textarea") return (
              <div key={f.k}>
                <label className="block text-xs font-mono uppercase tracking-widest text-white/70 mb-1">{f.label}</label>
                <textarea rows={f.rows || 3} value={form[f.k] ?? ""} onChange={e => setForm({...form, [f.k]: e.target.value})}
                  className="w-full bg-black border border-white/40 px-3 py-2 rounded text-white"/>
              </div>
            );
            if (f.type === "image") return (
              <div key={f.k}>
                <label className="block text-xs font-mono uppercase tracking-widest text-white/70 mb-1">{f.label}</label>
                <ImageField value={form[f.k] || ""} onChange={v => setForm({...form, [f.k]: v})} testid={`edit-${f.k}`}/>
              </div>
            );
            if (f.type === "pricing") return (
              <div key={f.k}>
                <label className="block text-xs font-mono uppercase tracking-widest text-white/70 mb-2">Prezzo</label>
                <div className="grid grid-cols-3 gap-2">
                  {[{k:"free",label:"Gratuito"},{k:"paid",label:"A pagamento"},{k:"request",label:"Su richiesta"}].map(o => (
                    <button key={o.k} type="button"
                      onClick={() => setForm({...form,
                        free: o.k==="free", price_on_request: o.k==="request",
                        price: o.k==="paid" ? (form.price || 0) : 0})}
                      className={`py-2 text-xs font-semibold uppercase tracking-wide border transition-colors ${
                        (form.free && o.k==="free") || (form.price_on_request && o.k==="request") ||
                        (!form.free && !form.price_on_request && o.k==="paid") ?
                        "bg-white text-black border-white" : "border-white/40 text-white hover:bg-white/10"
                      }`}>
                      {o.label}
                    </button>
                  ))}
                </div>
                {!form.free && !form.price_on_request && (
                  <input type="number" step="0.5" placeholder="Prezzo €" value={form.price ?? 0}
                    onChange={e => setForm({...form, price: parseFloat(e.target.value) || 0})}
                    className="mt-2 w-full bg-black border border-white/40 px-3 py-2 rounded text-white"/>
                )}
              </div>
            );
            if (f.type === "pricing_shop") return (
              <div key={f.k}>
                <label className="block text-xs font-mono uppercase tracking-widest text-white/70 mb-2">Prezzo</label>
                <div className="grid grid-cols-2 gap-2">
                  {[{k:"paid",label:"A pagamento"},{k:"request",label:"Su richiesta"}].map(o => (
                    <button key={o.k} type="button"
                      onClick={() => setForm({...form, price_on_request: o.k==="request",
                        price: o.k==="paid" ? (form.price || 0) : 0})}
                      className={`py-2 text-xs font-semibold uppercase tracking-wide border transition-colors ${
                        (form.price_on_request && o.k==="request") || (!form.price_on_request && o.k==="paid") ?
                        "bg-white text-black border-white" : "border-white/40 text-white hover:bg-white/10"
                      }`}>
                      {o.label}
                    </button>
                  ))}
                </div>
                {!form.price_on_request && (
                  <input type="number" step="0.5" placeholder="Prezzo €" value={form.price ?? 0}
                    onChange={e => setForm({...form, price: parseFloat(e.target.value) || 0})}
                    className="mt-2 w-full bg-black border border-white/40 px-3 py-2 rounded text-white"/>
                )}
              </div>
            );
            if (f.type === "checkbox") return (
              <label key={f.k} className="flex items-center gap-2 text-sm text-white">
                <input type="checkbox" checked={!!form[f.k]}
                  onChange={e => setForm({...form, [f.k]: e.target.checked})}
                  className="accent-white"/>
                {f.label}
              </label>
            );
            return (
              <div key={f.k}>
                <label className="block text-xs font-mono uppercase tracking-widest text-white/70 mb-1">{f.label}</label>
                <input type={f.type || "text"}
                  step={f.type === "number" ? (f.step || "0.5") : undefined}
                  value={f.type === "datetime-local" ?
                    (form[f.k] ? new Date(form[f.k]).toISOString().slice(0,16) : "") :
                    (form[f.k] ?? "")}
                  onChange={e => setForm({...form, [f.k]: e.target.value})}
                  className="w-full bg-black border border-white/40 px-3 py-2 rounded text-white"/>
              </div>
            );
          })}
        </div>
        <div className="mt-6 flex gap-2">
          <button onClick={save} disabled={busy} className="brutal-btn brutal-btn-primary flex-1" data-testid="edit-save">
            {busy ? "Salvo..." : "Salva modifiche"}
          </button>
          <button onClick={onClose} className="brutal-btn brutal-btn-outline">Annulla</button>
        </div>
      </div>
    </div>
  );
}

/* ---------- Shared file upload field ---------- */
function ImageField({ value, onChange, accept = "image/*", label = "Immagine", testid = "img-field" }) {
  const [busy, setBusy] = useState(false);
  const inputRef = React.useRef(null);
  const upload = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setBusy(true);
    try {
      const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const fullUrl = process.env.REACT_APP_BACKEND_URL + data.url;
      onChange(fullUrl);
      toast.success("File caricato");
    } catch (e) {
      toast.error("Errore upload: " + (e.response?.data?.detail || e.message));
    }
    setBusy(false);
  };
  const onDrop = (e) => { e.preventDefault(); const f = e.dataTransfer.files?.[0]; if (f) upload(f); };
  return (
    <div>
      <div className="flex items-stretch gap-2">
        <input placeholder="URL o carica dal computer" value={value || ""} onChange={e => onChange(e.target.value)}
          className="flex-1 bg-black border border-white/40 px-3 py-2 rounded text-white" data-testid={testid}/>
        <button type="button" onClick={() => inputRef.current?.click()} disabled={busy}
          className="brutal-btn brutal-btn-outline text-xs px-3 py-2" data-testid={`${testid}-btn`}>
          <Upload size={14}/> {busy ? "..." : "Carica"}
        </button>
        <input ref={inputRef} type="file" accept={accept} hidden
          onChange={e => upload(e.target.files?.[0])}/>
      </div>
      <div onDragOver={e => e.preventDefault()} onDrop={onDrop}
        className="mt-2 border border-dashed border-white/30 text-xs text-white/50 text-center py-2 rounded">
        {value ? <span className="text-white/70">Trascina un nuovo file qui per sostituire</span> : "o trascina un file qui"}
      </div>
      {value && !value.startsWith("blob:") && (
        <div className="mt-2 aspect-video bg-black border border-white/20">
          {accept.includes("video") ? (
            <video src={value} controls className="w-full h-full object-contain"/>
          ) : (
            <img src={value} alt={label} className="w-full h-full object-contain"/>
          )}
        </div>
      )}
    </div>
  );
}

/* ---------- MESSAGGI ---------- */
function MessaggiTab() {
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);
  const [filter, setFilter] = useState("all"); // all | unread

  const load = async () => {
    const { data } = await api.get("/admin/contact_messages");
    setItems(data);
  };
  useEffect(() => { load(); }, []);

  const markRead = async (m, read = true) => {
    await api.patch(`/admin/contact_messages/${m.id}`, { read });
    load();
    if (selected?.id === m.id) setSelected({ ...m, read });
  };
  const del = async (m) => {
    if (!confirm("Eliminare il messaggio?")) return;
    await api.delete(`/admin/contact_messages/${m.id}`);
    if (selected?.id === m.id) setSelected(null);
    load();
    toast.success("Messaggio eliminato");
  };
  const reply = (m) => {
    const subject = encodeURIComponent(`Re: ${m.subject}`);
    const body = encodeURIComponent(`\n\n---\nIl ${new Date(m.created_at).toLocaleString("it-IT")} ${m.name} <${m.email}> ha scritto:\n\n> ${m.message.split("\n").join("\n> ")}`);
    window.location.href = `mailto:${m.email}?subject=${subject}&body=${body}`;
    if (!m.read) markRead(m, true);
  };

  const filtered = filter === "unread" ? items.filter(m => !m.read) : items;
  const unreadCount = items.filter(m => !m.read).length;

  return (
    <div className="grid lg:grid-cols-5 gap-4 min-h-[500px]">
      {/* List */}
      <div className="lg:col-span-2">
        <div className="flex items-center justify-between mb-3">
          <div className="tag-pill" data-testid="msg-unread-count">
            <Inbox size={12}/> {unreadCount} da leggere · {items.length} totali
          </div>
          <select value={filter} onChange={e => setFilter(e.target.value)}
            className="bg-black border border-white/40 px-2 py-1 rounded text-xs text-white">
            <option value="all">Tutti</option>
            <option value="unread">Non letti</option>
          </select>
        </div>

        <div className="space-y-2 max-h-[70vh] overflow-y-auto pr-1">
          {filtered.length === 0 && (
            <div className="card p-8 text-center text-white/50 text-sm" data-testid="msg-empty">
              Nessun messaggio {filter === "unread" ? "non letto" : ""}
            </div>
          )}
          {filtered.map(m => (
            <button key={m.id} onClick={() => { setSelected(m); if (!m.read) markRead(m, true); }}
              data-testid={`msg-item-${m.id}`}
              className={`card p-3 w-full text-left transition-all ${selected?.id === m.id ? "bg-white/10" : ""} ${!m.read ? "border-l-4 border-l-white" : ""}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-white truncate">
                    {!m.read && <span className="inline-block w-2 h-2 rounded-full bg-white mr-2 align-middle"/>}
                    {m.name}
                  </div>
                  <div className="text-xs text-white/60 truncate">{m.email}</div>
                  <div className="text-sm text-white/80 mt-1 truncate font-medium">{m.subject}</div>
                  <div className="text-xs text-white/50 mt-1 line-clamp-2">{m.message}</div>
                </div>
                <div className="text-xs text-white/40 font-mono whitespace-nowrap">
                  {new Date(m.created_at).toLocaleDateString("it-IT", { day:"2-digit", month:"short"})}
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Detail */}
      <div className="lg:col-span-3">
        {selected ? (
          <div className="card p-6" data-testid="msg-detail">
            <div className="flex items-start justify-between gap-4 mb-4">
              <div className="min-w-0">
                <div className="text-xs font-mono uppercase tracking-widest text-white/50">
                  {new Date(selected.created_at).toLocaleString("it-IT")}
                </div>
                <h3 className="mt-1 font-display text-3xl uppercase text-white break-words">{selected.subject}</h3>
              </div>
              <div className="flex gap-2 shrink-0">
                <button onClick={() => markRead(selected, !selected.read)} title={selected.read ? "Segna come non letto" : "Segna come letto"}
                  className="w-9 h-9 flex items-center justify-center border border-white/40 hover:bg-white hover:text-black transition-colors">
                  <CheckCircle2 size={16}/>
                </button>
                <button onClick={() => del(selected)} title="Elimina"
                  className="w-9 h-9 flex items-center justify-center border border-white/40 hover:bg-white hover:text-black transition-colors">
                  <Trash2 size={16}/>
                </button>
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-3 mb-5">
              <div>
                <div className="text-xs font-mono uppercase tracking-widest text-white/50">Da</div>
                <div className="text-white">{selected.name}</div>
              </div>
              <div>
                <div className="text-xs font-mono uppercase tracking-widest text-white/50">Email</div>
                <a href={`mailto:${selected.email}`} className="text-white underline break-all">{selected.email}</a>
              </div>
              {selected.phone && (
                <div>
                  <div className="text-xs font-mono uppercase tracking-widest text-white/50">Telefono</div>
                  <a href={`tel:${selected.phone}`} className="text-white underline">{selected.phone}</a>
                </div>
              )}
            </div>

            <div className="border-t border-white/20 pt-4">
              <div className="text-xs font-mono uppercase tracking-widest text-white/50 mb-2">Messaggio</div>
              <p className="text-white whitespace-pre-line leading-relaxed">{selected.message}</p>
            </div>

            <div className="mt-6 flex flex-wrap gap-2">
              <button onClick={() => reply(selected)} className="brutal-btn brutal-btn-primary" data-testid="msg-reply">
                <Mail size={16}/> Rispondi via email
              </button>
              <a href={`mailto:${selected.email}`} className="brutal-btn brutal-btn-outline text-xs">
                Nuova email
              </a>
            </div>
          </div>
        ) : (
          <div className="card p-10 text-center h-full flex flex-col items-center justify-center">
            <Inbox size={48} className="text-white/30"/>
            <div className="mt-4 font-display text-2xl uppercase text-white/50">Seleziona un messaggio</div>
            <p className="text-white/40 text-sm mt-2">I contatti dal modulo del sito appariranno qui.</p>
          </div>
        )}
      </div>
    </div>
  );
}

/* ---------- SOCI ---------- */
function SociTab() {
  const [assoc, setAssoc] = useState("DAMACO_APS");
  const [q, setQ] = useState("");
  const [tessera, setTessera] = useState("");
  const [soci, setSoci] = useState([]);
  const [numModal, setNumModal] = useState(null);
  const [numInput, setNumInput] = useState("");

  const load = async () => {
    const params = new URLSearchParams();
    params.set("association", assoc);
    if (q) params.set("q", q);
    if (tessera) params.set("tessera", tessera);
    const { data } = await api.get("/admin/soci?" + params.toString());
    setSoci(data);
  };
  useEffect(() => { load(); }, [assoc, tessera]);

  const assignNumber = async () => {
    if (!numInput.trim()) return toast.error("Inserisci un numero tessera");
    await api.patch(`/admin/soci/${numModal.id}/tessera`, { numero_tessera: numInput.trim() });
    toast.success(`Tessera ${numInput} assegnata a ${numModal.nome} ${numModal.cognome}`);
    setNumModal(null); setNumInput(""); load();
  };

  const exportPdf = async () => {
    try {
      const res = await api.get(`/admin/soci/export.pdf?association=${assoc}`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `registro_soci_${assoc}_${new Date().toISOString().slice(0,10)}.pdf`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 1500);
    } catch (e) {
      toast.error("Errore durante il download del PDF");
    }
  };

  const deleteSocio = async (s) => {
    if (!window.confirm(`Eliminare definitivamente ${s.nome} ${s.cognome}?\nQuesta azione non può essere annullata.`)) return;
    try {
      await api.delete(`/admin/soci/${s.id}`);
      toast.success(`Socio ${s.nome} ${s.cognome} eliminato`);
      load();
    } catch (e) {
      toast.error("Impossibile eliminare il socio");
    }
  };

  return (
    <div>
      <div className="flex flex-wrap gap-2 mb-4">
        <select value={assoc} onChange={e => setAssoc(e.target.value)} data-testid="filter-assoc"
          className="bg-[#000000] border border-[#FFFFFF] px-3 py-2 rounded text-white">
          <option value="DAMACO_APS">DAMACO APS</option>
          <option value="ROCCA_APS">ROCCA APS</option>
        </select>
        <select value={tessera} onChange={e => setTessera(e.target.value)}
          className="bg-[#000000] border border-[#FFFFFF] px-3 py-2 rounded text-white">
          <option value="">Tutti</option>
          <option value="assigned">Con tessera</option>
          <option value="unassigned">Senza tessera</option>
        </select>
        <div className="flex-1 flex items-center gap-2 bg-[#000000] border border-[#FFFFFF] rounded px-3 max-w-md">
          <Search size={14} className="text-white/50"/>
          <input value={q} onChange={e => setQ(e.target.value)} onKeyDown={e => e.key==="Enter" && load()}
            placeholder="Cerca per nome, email, tessera..." data-testid="filter-search"
            className="flex-1 bg-transparent py-2 outline-none text-white"/>
          <button onClick={load} className="text-xs text-[#FFFFFF]">Cerca</button>
        </div>
        <button onClick={exportPdf} className="brutal-btn brutal-btn-outline text-xs" data-testid="export-pdf">
          <Download size={14}/> Esporta PDF
        </button>
      </div>

      <div className="card overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#000000] border-b border-[#FFFFFF]">
            <tr className="text-left text-xs uppercase font-mono text-white/70">
              <th className="p-3">Tessera</th><th className="p-3">Nome</th><th className="p-3">Email</th>
              <th className="p-3">Telefono</th><th className="p-3">Pagamento</th><th className="p-3">Data</th><th className="p-3">Azioni</th>
            </tr>
          </thead>
          <tbody>
            {soci.map(s => (
              <tr key={s.id} className="border-b border-[#FFFFFF] hover:bg-white/5" data-testid={`socio-row-${s.id}`}>
                <td className="p-3 font-mono">
                  {s.numero_tessera ? <span className="text-[#FFFFFF]">#{s.numero_tessera}</span> :
                    <span className="text-white/50">—</span>}
                </td>
                <td className="p-3">
                  <div className="font-semibold">{s.nome} {s.cognome}</div>
                  <div className="text-xs text-white/50">{s.genere} · {s.data_nascita}</div>
                </td>
                <td className="p-3 text-white">{s.email}</td>
                <td className="p-3 text-white">{s.telefono}</td>
                <td className="p-3">
                  <span className={`tag-pill ${s.stato_pagamento==="paid" ? "text-[#FFFFFF]" : s.stato_pagamento==="cash" ? "text-[#FFFFFF]" : "text-white/70"}`}>
                    {s.stato_pagamento}
                  </span>
                </td>
                <td className="p-3 text-xs text-white/50">{new Date(s.data_richiesta).toLocaleDateString("it-IT")}</td>
                <td className="p-3">
                  <div className="flex gap-2 flex-wrap">
                    <button onClick={() => { setNumModal(s); setNumInput(s.numero_tessera||""); }}
                      data-testid={`assign-${s.id}`}
                      className="brutal-btn brutal-btn-outline text-xs px-3 py-2">
                      {s.numero_tessera ? "Modifica" : "Assegna tessera"}
                    </button>
                    <button onClick={() => deleteSocio(s)}
                      data-testid={`delete-socio-${s.id}`}
                      title="Elimina socio"
                      className="brutal-btn brutal-btn-outline text-xs px-3 py-2">
                      <Trash2 size={14}/>
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {soci.length===0 && <tr><td colSpan={7} className="p-8 text-center text-white/50">Nessun socio</td></tr>}
          </tbody>
        </table>
      </div>

      {numModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4" onClick={() => setNumModal(null)}>
          <div className="card p-6 w-full max-w-sm" onClick={e => e.stopPropagation()}>
            <h3 className="font-display text-2xl uppercase">Assegna tessera</h3>
            <p className="mt-2 text-sm text-white/70">{numModal.nome} {numModal.cognome} — {numModal.email}</p>
            <input autoFocus value={numInput} onChange={e => setNumInput(e.target.value)}
              onKeyDown={e => e.key==="Enter" && assignNumber()} data-testid="tessera-number-input"
              placeholder="Es. 042"
              className="mt-4 w-full bg-[#000000] border border-[#FFFFFF] rounded px-3 py-3 text-white text-center font-mono text-xl"/>
            <button onClick={assignNumber} className="brutal-btn brutal-btn-primary w-full mt-4" data-testid="tessera-confirm">
              Conferma assegnazione
            </button>
            <button onClick={() => setNumModal(null)} className="w-full mt-2 text-xs text-white/50">Annulla</button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ---------- EVENTI ---------- */
function EventiTab() {
  const [events, setEvents] = useState([]);
  const [form, setForm] = useState({ title:"", description:"", date:"", location:"", price:"0", pricing:"free", poster_url:"" });
  const [galleryFor, setGalleryFor] = useState(null);
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/events").then(r => setEvents(r.data));
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.title || !form.date) return toast.error("Titolo e data obbligatori");
    try {
      const payload = {
        title: form.title, description: form.description, date: form.date, location: form.location,
        poster_url: form.poster_url,
        free: form.pricing === "free",
        price_on_request: form.pricing === "request",
        price: form.pricing === "paid" ? parseFloat(form.price || "0") : 0,
      };
      await api.post("/events", payload);
      toast.success("Evento creato"); setForm({ title:"", description:"", date:"", location:"", price:"0", pricing:"free", poster_url:"" });
      load();
    } catch { toast.error("Errore"); }
  };
  const del = async (id) => { if (!confirm("Eliminare?")) return; await api.delete(`/events/${id}`); load(); };
  const saveEdit = async (updated) => {
    const payload = {
      title: updated.title, description: updated.description, date: updated.date,
      location: updated.location, poster_url: updated.poster_url,
      free: !!updated.free, price_on_request: !!updated.price_on_request,
      price: !updated.free && !updated.price_on_request ? parseFloat(updated.price || 0) : 0,
    };
    await api.patch(`/events/${editing.id}`, payload); load();
  };

  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <div className="card p-5">
        <h3 className="font-display text-2xl uppercase mb-4">Nuovo evento</h3>
        <div className="grid gap-3">
          <input placeholder="Titolo" value={form.title} onChange={e => setForm({...form, title: e.target.value})} className="bg-[#000000] border border-[#FFFFFF] px-3 py-2 rounded" data-testid="ev-title"/>
          <textarea placeholder="Descrizione" rows={3} value={form.description} onChange={e => setForm({...form, description: e.target.value})} className="bg-[#000000] border border-[#FFFFFF] px-3 py-2 rounded"/>
          <input type="datetime-local" value={form.date} onChange={e => setForm({...form, date: e.target.value})} className="bg-[#000000] border border-[#FFFFFF] px-3 py-2 rounded" data-testid="ev-date"/>
          <input placeholder="Location" value={form.location} onChange={e => setForm({...form, location: e.target.value})} className="bg-[#000000] border border-[#FFFFFF] px-3 py-2 rounded"/>
          <div>
            <label className="block text-xs font-mono uppercase tracking-widest text-white mb-1">Locandina evento (formato quadrato, come post Instagram)</label>
            <ImageField value={form.poster_url} onChange={v => setForm({...form, poster_url: v})} testid="ev-poster"/>
          </div>
          <div>
            <label className="block text-xs font-mono uppercase tracking-widest text-white mb-2">Prezzo</label>
            <div className="grid grid-cols-3 gap-2">
              {[
                { k:"free", label:"Gratuito" },
                { k:"paid", label:"A pagamento" },
                { k:"request", label:"Su richiesta" },
              ].map(o => (
                <button key={o.k} type="button" onClick={() => setForm({...form, pricing: o.k})}
                  data-testid={`ev-pricing-${o.k}`}
                  className={`py-2 text-xs font-semibold uppercase tracking-wide border transition-colors ${
                    form.pricing===o.k ? "bg-white text-black border-white" : "border-white/40 text-white hover:bg-white/10"
                  }`}>
                  {o.label}
                </button>
              ))}
            </div>
          </div>
          {form.pricing === "paid" && (
            <input type="number" step="0.5" placeholder="Prezzo €" value={form.price} onChange={e => setForm({...form, price: e.target.value})} className="bg-[#000000] border border-[#FFFFFF] px-3 py-2 rounded"/>
          )}
          <button onClick={create} className="brutal-btn brutal-btn-primary" data-testid="ev-create">Crea evento</button>
        </div>
      </div>
      <div>
        <h3 className="font-display text-2xl uppercase mb-4">Eventi esistenti</h3>
        <div className="space-y-2">
          {events.map(e => (
            <div key={e.id} className="card p-3">
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-semibold">{e.title}</div>
                  <div className="text-xs text-white/50">
                    {new Date(e.date).toLocaleString("it-IT")} · {e.price_on_request ? "Su richiesta" : e.free ? "Gratis" : `€${e.price}`} · {(e.gallery||[]).length} foto
                  </div>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => setEditing(e)} className="text-xs text-white underline" data-testid={`edit-event-${e.id}`}>Modifica</button>
                  <button onClick={() => setGalleryFor(e)} className="text-xs text-white underline">Galleria</button>
                  <button onClick={() => del(e.id)} className="text-xs text-white hover:text-white">Elimina</button>
                </div>
              </div>
            </div>
          ))}
          {events.length===0 && <div className="text-white/50 text-sm">Nessun evento</div>}
        </div>
      </div>
      {galleryFor && <GalleryModal event={galleryFor} onClose={() => { setGalleryFor(null); load(); }}/>}
      {editing && <EditModal title={`Modifica: ${editing.title}`} item={editing} onSave={saveEdit} onClose={() => setEditing(null)}
        fields={[
          { k:"title", label:"Titolo" },
          { k:"description", label:"Descrizione", type:"textarea", rows:4 },
          { k:"date", label:"Data e ora", type:"datetime-local" },
          { k:"location", label:"Location" },
          { k:"poster_url", label:"Locandina (foto quadrata)", type:"image" },
          { k:"pricing", label:"Prezzo", type:"pricing" },
        ]}/>}
    </div>
  );
}

/* ---------- PODCAST ---------- */
function GalleryModal({ event, onClose }) {
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const add = async () => {
    if (!url) return;
    setBusy(true);
    const fd = new FormData(); fd.append("url", url);
    try {
      await api.post(`/events/${event.id}/gallery`, fd);
      toast.success("Aggiunto alla galleria"); setUrl("");
    } catch { toast.error("Errore"); }
    setBusy(false);
  };
  const uploadFile = async (file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    setBusy(true);
    try {
      const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const fullUrl = process.env.REACT_APP_BACKEND_URL + data.url;
      const gf = new FormData(); gf.append("url", fullUrl);
      await api.post(`/events/${event.id}/gallery`, gf);
      toast.success("Media aggiunto alla galleria");
    } catch (e) { toast.error("Errore upload"); }
    setBusy(false);
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4" onClick={onClose}>
      <div className="card p-6 w-full max-w-2xl max-h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <h3 className="font-display text-2xl uppercase text-white mb-2">Galleria: {event.title}</h3>
        <p className="text-xs text-white/50 mb-4">Aggiungi foto o video dello svolgimento dell'evento.</p>
        <div className="grid grid-cols-3 gap-2 mb-4">
          {(event.gallery || []).map((u, i) => (
            <div key={i} className="aspect-square bg-black border border-white/20">
              {u.match(/\.(mp4|webm|mov)/i) ?
                <video src={u} className="w-full h-full object-cover" controls/> :
                <img src={u} alt="" className="w-full h-full object-cover"/>}
            </div>
          ))}
        </div>
        <div className="grid gap-2">
          <input placeholder="URL foto/video" value={url} onChange={e => setUrl(e.target.value)}
            className="bg-black border border-white/40 px-3 py-2 rounded text-white"/>
          <div className="flex gap-2">
            <button onClick={add} disabled={busy} className="brutal-btn brutal-btn-outline text-xs flex-1">Aggiungi URL</button>
            <label className="brutal-btn brutal-btn-outline text-xs flex-1 cursor-pointer">
              <Upload size={14}/> Carica file
              <input type="file" accept="image/*,video/*" hidden onChange={e => uploadFile(e.target.files?.[0])}/>
            </label>
          </div>
          <button onClick={onClose} className="text-xs text-white/70 mt-2">Chiudi</button>
        </div>
      </div>
    </div>
  );
}

function PodcastTab() {
  const [eps, setEps] = useState([]);
  const [slots, setSlots] = useState([]);
  const [blocks, setBlocks] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title:"", description:"", stream_url:"", live:false, scheduled_at:"" });
  const [blockForm, setBlockForm] = useState({ type:"single", date:"", range_start:"", range_end:"", days_of_week:[], start:"09", end:"23", label:"" });
  const load = async () => {
    const [a, b, c] = await Promise.all([
      api.get("/podcast/episodes"),
      api.get("/podcast/slots"),
      api.get("/admin/podcast/blocks"),
    ]);
    setEps(a.data); setSlots(b.data); setBlocks(c.data);
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.title) return toast.error("Titolo obbligatorio");
    await api.post("/podcast/episodes", form);
    toast.success("Episodio creato"); setForm({ title:"", description:"", stream_url:"", live:false, scheduled_at:"" });
    load();
  };
  const toggleLive = async (ep) => { await api.patch(`/podcast/episodes/${ep.id}`, { live: !ep.live }); load(); };
  const del = async (id) => { if(!confirm("Eliminare?")) return; await api.delete(`/podcast/episodes/${id}`); load(); };
  const updateSlot = async (id, status) => { await api.patch(`/podcast/slots/${id}`, { status }); load(); };

  const addBlock = async () => {
    try {
      if (blockForm.type === "single") {
        if (!blockForm.date) return toast.error("Scegli una data");
        const sh = parseInt(blockForm.start), eh = parseInt(blockForm.end);
        if (isNaN(sh) || isNaN(eh) || eh <= sh) return toast.error("Orario di fine deve essere dopo l'inizio");
        const start = new Date(`${blockForm.date}T${String(sh).padStart(2,"0")}:00:00`).toISOString();
        const end = new Date(`${blockForm.date}T${String(eh).padStart(2,"0")}:00:00`).toISOString();
        await api.post("/admin/podcast/blocks", { type:"single", start, end, label: blockForm.label || "Non disponibile" });
      } else if (blockForm.type === "range") {
        if (!blockForm.range_start || !blockForm.range_end) return toast.error("Scegli data inizio e fine");
        if (blockForm.range_end < blockForm.range_start) return toast.error("La data di fine deve essere dopo l'inizio");
        await api.post("/admin/podcast/blocks", { type:"range", start: blockForm.range_start, end: blockForm.range_end, label: blockForm.label || "Chiusura prolungata" });
      } else if (blockForm.type === "weekly") {
        if (!blockForm.days_of_week?.length) return toast.error("Scegli almeno un giorno della settimana");
        if (!blockForm.range_start || !blockForm.range_end) return toast.error("Scegli inizio e fine ricorrenza");
        const hs = parseInt(blockForm.start), he = parseInt(blockForm.end);
        if (isNaN(hs) || isNaN(he) || he <= hs) return toast.error("Orario di fine deve essere dopo l'inizio");
        await api.post("/admin/podcast/blocks", {
          type:"weekly", start: blockForm.range_start, end: blockForm.range_end,
          days_of_week: blockForm.days_of_week, hour_start: hs, hour_end: he,
          label: blockForm.label || "Chiuso"
        });
      }
      toast.success("Blocco creato");
      setBlockForm({ type:"single", date:"", range_start:"", range_end:"", days_of_week:[], start:"09", end:"23", label:"" });
      load();
    } catch { toast.error("Errore"); }
  };
  const delBlock = async (id) => { if(!confirm("Rimuovere il blocco?"))return; await api.delete(`/admin/podcast/blocks/${id}`); load(); };
  const toggleDow = (d) => {
    setBlockForm(f => ({...f, days_of_week: f.days_of_week.includes(d) ? f.days_of_week.filter(x => x!==d) : [...f.days_of_week, d].sort()}));
  };

  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <div className="card p-5">
        <h3 className="font-display text-2xl uppercase mb-4">Nuovo episodio</h3>
        <div className="grid gap-3">
          <input placeholder="Titolo" value={form.title} onChange={e => setForm({...form, title: e.target.value})} className="bg-[#000000] border border-[#FFFFFF] px-3 py-2 rounded" data-testid="pod-title"/>
          <textarea rows={2} placeholder="Descrizione" value={form.description} onChange={e => setForm({...form, description: e.target.value})} className="bg-[#000000] border border-[#FFFFFF] px-3 py-2 rounded"/>
          <input placeholder="URL YouTube/Twitch (live o registrazione)" value={form.stream_url} onChange={e => setForm({...form, stream_url: e.target.value})} className="bg-[#000000] border border-[#FFFFFF] px-3 py-2 rounded" data-testid="pod-url"/>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.live} onChange={e => setForm({...form, live: e.target.checked})} className="accent-[#FFFFFF]"/>
            In diretta ora
          </label>
          <button onClick={create} className="brutal-btn brutal-btn-primary" data-testid="pod-create">Pubblica</button>
        </div>

        <h3 className="font-display text-2xl uppercase mt-8 mb-3">Episodi</h3>
        <div className="space-y-2">
          {eps.map(e => (
            <div key={e.id} className="card p-3 flex items-center justify-between">
              <div>
                <div className="font-semibold">{e.title} {e.live && <span className="ml-2 text-white text-xs">● LIVE</span>}</div>
                <div className="text-xs text-white/50 truncate max-w-[300px]">{e.stream_url}</div>
              </div>
              <div className="flex gap-2">
                <button onClick={() => setEditing(e)} className="text-xs text-white underline" data-testid={`edit-ep-${e.id}`}>Modifica</button>
                <button onClick={() => toggleLive(e)} className="text-xs text-[#FFFFFF]">
                  {e.live ? "Stop live" : "Vai live"}
                </button>
                <button onClick={() => del(e.id)} className="text-xs text-white">Elimina</button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div>
        <h3 className="font-display text-2xl uppercase mb-4">Richieste slot</h3>
        <div className="space-y-2">
          {slots.map(s => (
            <div key={s.id} className="card p-3">
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-semibold">{s.guest_name} <span className="text-xs text-white/50">({s.guest_email})</span></div>
                  <div className="text-xs text-white/70">{new Date(s.date).toLocaleString("it-IT")}</div>
                  <div className="text-sm text-white mt-1">{s.topic}</div>
                </div>
                <span className={`tag-pill ${s.status==="confirmed"?"text-[#FFFFFF]":s.status==="declined"?"text-white":"text-white/70"}`}>{s.status}</span>
              </div>
              {s.status === "pending" && (
                <div className="mt-3 flex gap-2">
                  <button onClick={() => updateSlot(s.id, "confirmed")} className="text-xs text-[#FFFFFF]">Conferma</button>
                  <button onClick={() => updateSlot(s.id, "declined")} className="text-xs text-white">Rifiuta</button>
                </div>
              )}
            </div>
          ))}
          {slots.length===0 && <div className="text-white/50 text-sm">Nessuna richiesta</div>}
        </div>

        {/* Admin blocks manager */}
        <h3 className="font-display text-2xl uppercase mt-8 mb-3">Blocca disponibilità</h3>
        <p className="text-xs text-white/50 mb-3">Blocca giorni singoli, intervalli di chiusura o ricorrenze settimanali (es. tutti i lunedì).</p>
        <div className="card p-4 grid gap-3">
          {/* Mode selector */}
          <div className="grid grid-cols-3 gap-2">
            {[
              { k:"single", label:"Un giorno" },
              { k:"range", label:"Intervallo" },
              { k:"weekly", label:"Ricorrente" },
            ].map(o => (
              <button key={o.k} type="button" onClick={() => setBlockForm({...blockForm, type: o.k})}
                data-testid={`block-mode-${o.k}`}
                className={`py-2 text-xs font-semibold uppercase tracking-wide border transition-colors ${
                  blockForm.type===o.k ? "bg-white text-black border-white" : "border-white/40 text-white hover:bg-white/10"
                }`}>
                {o.label}
              </button>
            ))}
          </div>

          {/* Single day */}
          {blockForm.type === "single" && (
            <>
              <input type="date" value={blockForm.date} onChange={e => setBlockForm({...blockForm, date: e.target.value})}
                className="bg-black border border-white/40 px-3 py-2 rounded text-white" data-testid="block-date"/>
              <div className="grid grid-cols-2 gap-2">
                <select value={blockForm.start} onChange={e => setBlockForm({...blockForm, start: e.target.value})}
                  className="bg-black border border-white/40 px-3 py-2 rounded text-white" data-testid="block-start">
                  {Array.from({length:15},(_,i)=>i+9).map(h => <option key={h} value={String(h).padStart(2,"0")}>{String(h).padStart(2,"0")}:00</option>)}
                </select>
                <select value={blockForm.end} onChange={e => setBlockForm({...blockForm, end: e.target.value})}
                  className="bg-black border border-white/40 px-3 py-2 rounded text-white" data-testid="block-end">
                  {Array.from({length:15},(_,i)=>i+10).map(h => <option key={h} value={String(h).padStart(2,"0")}>{String(h).padStart(2,"0")}:00</option>)}
                </select>
              </div>
            </>
          )}

          {/* Date range */}
          {blockForm.type === "range" && (
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-[10px] font-mono uppercase tracking-widest text-white/60 mb-1">Da</label>
                <input type="date" value={blockForm.range_start} onChange={e => setBlockForm({...blockForm, range_start: e.target.value})}
                  className="w-full bg-black border border-white/40 px-3 py-2 rounded text-white" data-testid="block-range-start"/>
              </div>
              <div>
                <label className="block text-[10px] font-mono uppercase tracking-widest text-white/60 mb-1">A</label>
                <input type="date" value={blockForm.range_end} onChange={e => setBlockForm({...blockForm, range_end: e.target.value})}
                  className="w-full bg-black border border-white/40 px-3 py-2 rounded text-white" data-testid="block-range-end"/>
              </div>
            </div>
          )}

          {/* Weekly recurrence */}
          {blockForm.type === "weekly" && (
            <>
              <div>
                <label className="block text-[10px] font-mono uppercase tracking-widest text-white/60 mb-1">Giorni della settimana</label>
                <div className="grid grid-cols-7 gap-1">
                  {["Lun","Mar","Mer","Gio","Ven","Sab","Dom"].map((d, i) => (
                    <button key={i} type="button" onClick={() => toggleDow(i)}
                      data-testid={`block-dow-${i}`}
                      className={`py-2 text-xs font-mono uppercase border transition-colors ${
                        blockForm.days_of_week.includes(i) ? "bg-white text-black border-white" : "border-white/40 text-white hover:bg-white/10"
                      }`}>
                      {d}
                    </button>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-[10px] font-mono uppercase tracking-widest text-white/60 mb-1">Da (data)</label>
                  <input type="date" value={blockForm.range_start} onChange={e => setBlockForm({...blockForm, range_start: e.target.value})}
                    className="w-full bg-black border border-white/40 px-3 py-2 rounded text-white" data-testid="block-weekly-start"/>
                </div>
                <div>
                  <label className="block text-[10px] font-mono uppercase tracking-widest text-white/60 mb-1">A (data)</label>
                  <input type="date" value={blockForm.range_end} onChange={e => setBlockForm({...blockForm, range_end: e.target.value})}
                    className="w-full bg-black border border-white/40 px-3 py-2 rounded text-white" data-testid="block-weekly-end"/>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <select value={blockForm.start} onChange={e => setBlockForm({...blockForm, start: e.target.value})}
                  className="bg-black border border-white/40 px-3 py-2 rounded text-white">
                  {Array.from({length:24},(_,i)=>i).map(h => <option key={h} value={String(h).padStart(2,"0")}>{String(h).padStart(2,"0")}:00</option>)}
                </select>
                <select value={blockForm.end} onChange={e => setBlockForm({...blockForm, end: e.target.value})}
                  className="bg-black border border-white/40 px-3 py-2 rounded text-white">
                  {Array.from({length:24},(_,i)=>i+1).map(h => <option key={h} value={String(h).padStart(2,"0")}>{String(h).padStart(2,"0")}:00</option>)}
                </select>
              </div>
            </>
          )}

          <input placeholder="Etichetta (opzionale)" value={blockForm.label} onChange={e => setBlockForm({...blockForm, label: e.target.value})}
            className="bg-black border border-white/40 px-3 py-2 rounded text-white"/>
          <button onClick={addBlock} className="brutal-btn brutal-btn-outline text-xs" data-testid="block-add">Blocca</button>
        </div>

        <div className="mt-3 space-y-2">
          {blocks.map(b => (
            <div key={b.id} className="card p-3 flex items-center justify-between" data-testid={`block-${b.id}`}>
              <div className="min-w-0">
                <div className="text-sm font-semibold flex items-center gap-2">
                  <span className="text-[10px] font-mono uppercase tracking-widest border border-white/40 px-1.5 py-0.5">
                    {b.type === "range" ? "Intervallo" : b.type === "weekly" ? "Ricorrente" : "Singolo"}
                  </span>
                  {b.label}
                </div>
                <div className="text-xs text-white/60 font-mono mt-1">
                  {b.type === "single" ?
                    `${new Date(b.start).toLocaleString("it-IT",{day:"2-digit",month:"short",hour:"2-digit",minute:"2-digit"})} → ${new Date(b.end).toLocaleString("it-IT",{hour:"2-digit",minute:"2-digit"})}` :
                   b.type === "range" ?
                    `${new Date(b.start).toLocaleDateString("it-IT")} → ${new Date(b.end).toLocaleDateString("it-IT")}` :
                    `${(b.days_of_week||[]).map(i => ["Lun","Mar","Mer","Gio","Ven","Sab","Dom"][i]).join(", ")} · ${String(b.hour_start).padStart(2,"0")}:00-${String(b.hour_end).padStart(2,"0")}:00 · fino ${new Date(b.end).toLocaleDateString("it-IT")}`
                  }
                </div>
              </div>
              <button onClick={() => delBlock(b.id)} className="text-xs text-white underline shrink-0 ml-2">Rimuovi</button>
            </div>
          ))}
          {blocks.length === 0 && <div className="text-white/50 text-sm">Nessun blocco attivo</div>}
        </div>
      </div>
      {editing && <EditModal title={`Modifica episodio: ${editing.title}`} item={editing}
        onSave={async u => { await api.patch(`/podcast/episodes/${editing.id}`, {
          title: u.title, description: u.description, stream_url: u.stream_url, live: !!u.live,
        }); load(); }}
        onClose={() => setEditing(null)}
        fields={[
          { k:"title", label:"Titolo" },
          { k:"description", label:"Descrizione", type:"textarea", rows:4 },
          { k:"stream_url", label:"URL YouTube/Twitch" },
          { k:"live", label:"In diretta ora", type:"checkbox" },
        ]}/>}
    </div>
  );
}

/* ---------- CORSI ---------- */
function CorsiTab() {
  const [corsi, setCorsi] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title:"", description:"", schedule:"", price:"0", pricing:"free", poster_url:"" });
  const load = () => api.get("/corsi").then(r => setCorsi(r.data));
  useEffect(() => { load(); }, []);
  const create = async () => {
    if (!form.title) return toast.error("Titolo obbligatorio");
    await api.post("/corsi", {
      title: form.title, description: form.description, schedule: form.schedule, poster_url: form.poster_url,
      price: form.pricing === "paid" ? parseFloat(form.price || "0") : 0,
      price_on_request: form.pricing === "request",
    });
    toast.success("Corso creato");
    setForm({ title:"", description:"", schedule:"", price:"0", pricing:"free", poster_url:"" }); load();
  };
  const del = async (id) => { if(!confirm("Eliminare?"))return; await api.delete(`/corsi/${id}`); load(); };
  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <div className="card p-5">
        <h3 className="font-display text-2xl uppercase mb-4">Nuovo corso</h3>
        <div className="grid gap-3">
          <input placeholder="Titolo" value={form.title} onChange={e => setForm({...form, title: e.target.value})} className="bg-[#000000] border border-[#FFFFFF] px-3 py-2 rounded" data-testid="cor-title"/>
          <textarea rows={2} placeholder="Descrizione" value={form.description} onChange={e => setForm({...form, description: e.target.value})} className="bg-[#000000] border border-[#FFFFFF] px-3 py-2 rounded"/>
          <input placeholder="Orario/Calendario" value={form.schedule} onChange={e => setForm({...form, schedule: e.target.value})} className="bg-[#000000] border border-[#FFFFFF] px-3 py-2 rounded"/>
          <div>
            <label className="block text-xs font-mono uppercase tracking-widest text-white mb-1">Immagine corso (formato quadrato)</label>
            <ImageField value={form.poster_url} onChange={v => setForm({...form, poster_url: v})} testid="cor-poster"/>
          </div>
          <div>
            <label className="block text-xs font-mono uppercase tracking-widest text-white mb-2">Prezzo</label>
            <div className="grid grid-cols-3 gap-2">
              {[
                { k:"free", label:"Gratuito" },
                { k:"paid", label:"A pagamento" },
                { k:"request", label:"Su richiesta" },
              ].map(o => (
                <button key={o.k} type="button" onClick={() => setForm({...form, pricing: o.k})}
                  data-testid={`cor-pricing-${o.k}`}
                  className={`py-2 text-xs font-semibold uppercase tracking-wide border transition-colors ${
                    form.pricing===o.k ? "bg-white text-black border-white" : "border-white/40 text-white hover:bg-white/10"
                  }`}>
                  {o.label}
                </button>
              ))}
            </div>
          </div>
          {form.pricing === "paid" && (
            <input type="number" step="0.5" placeholder="Prezzo €" value={form.price} onChange={e => setForm({...form, price: e.target.value})} className="bg-[#000000] border border-[#FFFFFF] px-3 py-2 rounded"/>
          )}
          <button onClick={create} className="brutal-btn brutal-btn-primary" data-testid="cor-create">Crea corso</button>
        </div>
      </div>
      <div>
        <h3 className="font-display text-2xl uppercase mb-4">Corsi attivi</h3>
        <div className="space-y-2">
          {corsi.map(c => (
            <div key={c.id} className="card p-3">
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-semibold">{c.title}</div>
                  <div className="text-xs text-white/50">{c.schedule} · {c.price_on_request ? "Su richiesta" : `€${c.price}`}</div>
                </div>
                <div className="flex gap-3">
                  <button onClick={() => setEditing({...c, free: !c.price_on_request && c.price===0})} className="text-xs text-white underline" data-testid={`edit-corso-${c.id}`}>Modifica</button>
                  <a href={`#`} onClick={async e => {
                    e.preventDefault();
                    const {data} = await api.get(`/admin/corsi/${c.id}/iscrizioni`);
                    alert(`Iscritti: ${data.length}\n\n${data.map(x => `${x.nome} ${x.cognome} — ${x.email}`).join("\n")}`);
                  }} className="text-xs text-[#FFFFFF]">Iscritti</a>
                  <button onClick={() => del(c.id)} className="text-xs text-white">Elimina</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      {editing && <EditModal title={`Modifica corso: ${editing.title}`} item={editing}
        onSave={async u => { await api.patch(`/corsi/${editing.id}`, {
          title: u.title, description: u.description, schedule: u.schedule, poster_url: u.poster_url,
          price: parseFloat(u.price || 0), price_on_request: !!u.price_on_request,
        }); load(); }}
        onClose={() => setEditing(null)}
        fields={[
          { k:"title", label:"Titolo" },
          { k:"description", label:"Descrizione", type:"textarea", rows:4 },
          { k:"schedule", label:"Orario/Calendario" },
          { k:"poster_url", label:"Immagine (foto quadrata)", type:"image" },
          { k:"pricing", label:"Prezzo", type:"pricing" },
        ]}/>}
    </div>
  );
}
/* ---------- SHOP ---------- */
function ShopTab() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name:"", description:"", price:"0", stock:"10", pricing:"paid", image_url:"" });
  const load = () => api.get("/merch").then(r => setItems(r.data));
  useEffect(() => { load(); }, []);
  const create = async () => {
    if (!form.name) return toast.error("Nome obbligatorio");
    await api.post("/merch", {
      name: form.name, description: form.description, image_url: form.image_url, stock: parseInt(form.stock),
      price: form.pricing === "paid" ? parseFloat(form.price) : 0,
      price_on_request: form.pricing === "request",
    });
    toast.success("Prodotto creato"); setForm({ name:"", description:"", price:"0", stock:"10", pricing:"paid", image_url:"" }); load();
  };
  const del = async (id) => { if(!confirm("Eliminare?"))return; await api.delete(`/merch/${id}`); load(); };
  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <div className="card p-5">
        <h3 className="font-display text-2xl uppercase mb-4">Nuovo prodotto</h3>
        <div className="grid gap-3">
          <input placeholder="Nome" value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="bg-[#000000] border border-[#FFFFFF] px-3 py-2 rounded" data-testid="prod-name"/>
          <textarea rows={2} placeholder="Descrizione" value={form.description} onChange={e => setForm({...form, description: e.target.value})} className="bg-[#000000] border border-[#FFFFFF] px-3 py-2 rounded"/>
          <div>
            <label className="block text-xs font-mono uppercase tracking-widest text-white mb-2">Prezzo</label>
            <div className="grid grid-cols-2 gap-2">
              {[
                { k:"paid", label:"A pagamento" },
                { k:"request", label:"Su richiesta" },
              ].map(o => (
                <button key={o.k} type="button" onClick={() => setForm({...form, pricing: o.k})}
                  data-testid={`prod-pricing-${o.k}`}
                  className={`py-2 text-xs font-semibold uppercase tracking-wide border transition-colors ${
                    form.pricing===o.k ? "bg-white text-black border-white" : "border-white/40 text-white hover:bg-white/10"
                  }`}>
                  {o.label}
                </button>
              ))}
            </div>
          </div>
          {form.pricing === "paid" && (
            <input type="number" step="0.5" placeholder="Prezzo €" value={form.price} onChange={e => setForm({...form, price: e.target.value})} className="bg-[#000000] border border-[#FFFFFF] px-3 py-2 rounded"/>
          )}
          <input type="number" placeholder="Stock" value={form.stock} onChange={e => setForm({...form, stock: e.target.value})} className="bg-[#000000] border border-[#FFFFFF] px-3 py-2 rounded"/>
          <div>
            <label className="block text-xs font-mono uppercase tracking-widest text-white mb-1">Immagine prodotto (formato quadrato)</label>
            <ImageField value={form.image_url} onChange={v => setForm({...form, image_url: v})} testid="prod-image"/>
          </div>
          <button onClick={create} className="brutal-btn brutal-btn-primary" data-testid="prod-create">Aggiungi</button>
        </div>
      </div>
      <div>
        <h3 className="font-display text-2xl uppercase mb-4">Catalogo</h3>
        <div className="space-y-2">
          {items.map(p => (
            <div key={p.id} className="card p-3 flex items-center justify-between">
              <div>
                <div className="font-semibold">{p.name}</div>
                <div className="text-xs text-white/50">{p.price_on_request ? "Su richiesta" : `€${p.price}`} · Stock: {p.stock}</div>
              </div>
              <button onClick={() => setEditing(p)} className="text-xs text-white underline mr-3" data-testid={`edit-prod-${p.id}`}>Modifica</button>
              <button onClick={() => del(p.id)} className="text-xs text-white">Elimina</button>
            </div>
          ))}
        </div>
      </div>
      {editing && <EditModal title={`Modifica prodotto: ${editing.name}`} item={editing}
        onSave={async u => { await api.patch(`/merch/${editing.id}`, {
          name: u.name, description: u.description, image_url: u.image_url,
          stock: parseInt(u.stock || 0), price: parseFloat(u.price || 0),
          price_on_request: !!u.price_on_request,
        }); load(); }}
        onClose={() => setEditing(null)}
        fields={[
          { k:"name", label:"Nome" },
          { k:"description", label:"Descrizione", type:"textarea", rows:3 },
          { k:"image_url", label:"Immagine (foto quadrata)", type:"image" },
          { k:"pricing", label:"Prezzo", type:"pricing_shop" },
          { k:"stock", label:"Stock", type:"number", step:"1" },
        ]}/>}
    </div>
  );
}
